import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Download,
  Search,
  X,
} from "lucide-react";
import { useMutation, useQuery } from "react-query";
import {
  UserNotification,
  SubscriptionHandle,
  listNotifications,
  markNotificationsRead,
  subscribeToNotifications,
} from "../api/notifications";
import { useAuthStore } from "../store";

const NOTIFICATION_PAGE_SIZE = 50;
const NOTIFICATION_HISTORY_CAP = 100;

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const sourceRef = useRef<SubscriptionHandle | null>(null);
  const { isAuthenticated, accessToken } = useAuthStore();

  const { data: initial } = useQuery({
    queryKey: ["userNotifications", "initial"],
    queryFn: () => listNotifications({ limit: NOTIFICATION_PAGE_SIZE }),
    enabled: !!isAuthenticated,
    staleTime: Infinity,
    refetchOnMount: true,
    retry: false,
  });

  useEffect(() => {
    if (!initial) return;
    setNotifications((prev) => {
      const seen = new Set(prev.map((n) => n.id));
      const merged = [...prev];
      for (const item of initial.items) {
        if (!seen.has(item.id)) merged.push(item);
      }
      return merged
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        )
        .slice(0, NOTIFICATION_HISTORY_CAP);
    });
    setUnread(initial.unreadCount);
  }, [initial]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const source = subscribeToNotifications({
      onOpen: () => setConnected(true),
      onError: () => setConnected(false),
      onMessage: (n) => {
        setNotifications((prev) => {
          if (prev.some((x) => x.id === n.id)) return prev;
          return [n, ...prev].slice(0, NOTIFICATION_HISTORY_CAP);
        });
        if (!n.isRead) setUnread((c) => c + 1);
      },
    });
    sourceRef.current = source;
    return () => {
      source?.close();
      sourceRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated, accessToken]);

  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  const filterOptions = useMemo(() => {
    const presentTypes = Array.from(
      new Set(notifications.map((n) => n.eventType)),
    );
    return ["ALL", "ERROR", ...presentTypes];
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter((n) => {
      if (activeFilter === "ERROR" && n.eventLevel !== "ERROR") return false;
      if (
        activeFilter !== "ALL" &&
        activeFilter !== "ERROR" &&
        n.eventType !== activeFilter
      )
        return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const text = n.message.replace(/<[^>]*>/g, "").toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [notifications, activeFilter, search]);

  const getFilterLabel = (f: string) => {
    if (f === "ALL") return "All";
    if (f === "ERROR") return "Errors";
    return typeStyles[f]?.label ?? f;
  };

  const markAllRead = useMutation({
    mutationFn: () => markNotificationsRead({ all: true }),
  });

  const handleOpen = () => {
    if (unread === 0) return;
    setUnread(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    markAllRead.mutate();
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const typeStyles: Record<
    string,
    { dot: string; label: string; chip: string }
  > = {
    DOWNLOAD: {
      dot: "bg-green-500",
      label: "Download Ready",
      chip: "bg-green-50 text-green-700",
    },
    CAPTABLE: {
      dot: "bg-purple-500",
      label: "Captable Ready",
      chip: "bg-purple-50 text-purple-700",
    },
    "data-pipeline": {
      dot: "bg-blue-500",
      label: "Data Pipeline",
      chip: "bg-blue-50 text-blue-700",
    },
    "task-complete": {
      dot: "bg-green-500",
      label: "Task Complete",
      chip: "bg-green-50 text-green-700",
    },
    alert: {
      dot: "bg-red-500",
      label: "Alert",
      chip: "bg-red-50 text-red-700",
    },
    notification: {
      dot: "bg-gray-400",
      label: "Notification",
      chip: "bg-gray-100 text-gray-700",
    },
    MCA_ORDER: {
      dot: "bg-orange-500",
      label: "MCA Order",
      chip: "bg-orange-50 text-orange-700",
    },
  };

  const errorLabels: Record<string, string> = {
    DOWNLOAD: "Download Failed",
    CAPTABLE: "Captable Failed",
    "data-pipeline": "Pipeline Failed",
    "task-complete": "Task Failed",
    MCA_ORDER: "MCA Order Failed",
  };

  const getTypeStyle = (n: UserNotification) => {
    if (n.eventLevel === "ERROR")
      return {
        dot: "bg-red-500",
        label: errorLabels[n.eventType] ?? "Error",
        chip: "bg-red-50 text-red-700",
      };
    return typeStyles[n.eventType] ?? typeStyles.notification;
  };

  const extractDownload = (
    n: UserNotification,
  ): { fileUrl: string; fileName?: string } | null => {
    if (n.eventLevel === "ERROR") return null;
    const ed = n.eventData ?? {};
    const fileUrl: string | undefined = ed.fileUrl;
    if (!fileUrl) return null;
    return { fileUrl, fileName: ed.fileName };
  };

  const getDisplayMessage = (n: UserNotification): string => {
    if (n.eventType === "DOWNLOAD" && n.eventLevel === "ERROR") {
      const fileName: string | undefined = n.eventData?.fileName;
      return fileName
        ? `We couldn't prepare <b>${fileName}</b>. Please try again.`
        : "We couldn't prepare your download. Please try again.";
    }
    return n.message;
  };

  const handleDownload = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Menu as="div" className="relative ml-3">
      <Menu.Button
        onClick={handleOpen}
        className="relative flex items-center justify-center rounded-full p-2 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        title={connected ? "Notifications" : "Disconnected"}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-secondary" />
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-20 mt-2 w-[32rem] origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">
              Notifications
            </span>
            {notifications.length > 0 && (
              <button
                onClick={() => {
                  setNotifications([]);
                  setUnread(0);
                }}
                className="text-xs text-blue-600 hover:underline"
              >
                Clear
              </button>
            )}
          </div>

          {/* Search */}
          <div className="px-3 pt-2 pb-1">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notifications…"
                className="w-full rounded border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-7 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex gap-1.5 overflow-x-auto px-3 pb-2 pt-1 scrollbar-hide">
            {filterOptions.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
                  activeFilter === f
                    ? f === "ERROR"
                      ? "bg-red-500 text-white"
                      : "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {getFilterLabel(f)}
                {f === "ERROR" && (
                  <span className="ml-1">
                    (
                    {
                      notifications.filter((n) => n.eventLevel === "ERROR")
                        .length
                    }
                    )
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="max-h-80 overflow-y-auto border-t border-gray-100">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-gray-500">
                {connected ? "No notifications yet" : "Connecting..."}
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-gray-400">
                No notifications match your filter
              </div>
            ) : (
              filteredNotifications.map((n) => {
                const style = getTypeStyle(n);
                const download = extractDownload(n);
                return (
                  <Menu.Item key={n.id}>
                    {({ active }) => (
                      <div
                        className={`flex gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 ${
                          active ? "bg-gray-50" : "bg-white"
                        }`}
                      >
                        <span
                          className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${style.dot}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${style.chip}`}
                            >
                              {n.eventLevel === "ERROR" ? (
                                <AlertCircle className="h-3 w-3" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              {style.label}
                            </span>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">
                              {formatTime(n.timestamp)}
                            </span>
                          </div>
                          <div
                            className="mt-0.5 text-xs text-gray-600 break-words leading-relaxed"
                            // Server emits HTML-safe message strings with
                            // inline tags like <b>...</b> for filenames.
                            dangerouslySetInnerHTML={{
                              __html: getDisplayMessage(n),
                            }}
                          />
                          {download && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(download.fileUrl);
                              }}
                              className="mt-2 inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-blue-700"
                              title={download.fileName ?? "Download"}
                            >
                              <Download className="h-3.5 w-3.5" />
                              Download
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Menu.Item>
                );
              })
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
